/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import AccessibilityCss from "components/accessibility/Accessibility.css";
import { getPagesViewElementHandle } from "selectors/pagedViewState";
import { getPagesInDOM, getTotalPages } from "selectors/documentSelectors";
import { getFocusableDescendants } from "utils/AccessibilityUtils";

export const getCurrActiveElemNum = stores => stores.accessibilityStore.currActiveElemNum;
export const shouldFetchAccContent = stores => stores.accessibilityStore.shouldFetchAccContent;
export const isAccEnabledByDefault = stores => stores.accessibilityStore.isAccEnabledByDefault;
export const getAccContainerElementHandle = (index, global) => {
  if (!global) {
    global = document;
  }
  const cssClass = AccessibilityCss.pageSizeContainer;
  const AccContainers = global.getElementsByClassName(cssClass);
  if (index < AccContainers.length) {
    return AccContainers[index];
  }
  return null;
};

// getElementToFocus provides element to be focussed in the pdf content, whereas
// getUIElementToFocus provides element to be focussed in the UI
export const getElementToFocus = stores => stores.accessibilityStore.elementToFocus;
export const getUIElementToFocus = stores => stores.accessibilityStore.uiElementToFocus;

export const getPageIndexToFocus = stores => stores.accessibilityStore.pageIndexToFocus;
export const getTabbablePDFElementsInPage = (stores, pageIndex) =>
  pageIndex < stores.accessibilityStore.tabbablePDFElementsInPage.length &&
  stores.accessibilityStore.tabbablePDFElementsInPage[pageIndex];
export const getIsActiveElementInPagesView = stores => stores.accessibilityStore.isActiveElementInPagesView;
export const shouldRenderTabbingSupport = stores => {
  if (!shouldFetchAccContent(stores) || getIsActiveElementInPagesView(stores)) return false;

  const pagesViewElement = getPagesViewElementHandle(stores);
  const tabbableContentInDOM = pagesViewElement && getFocusableDescendants(pagesViewElement);
  if (tabbableContentInDOM && tabbableContentInDOM.length) return true;

  const { start: firstPageInDOM, end: lastPageInDOM } = getPagesInDOM(stores);
  for (let pageIndex = 0; pageIndex < firstPageInDOM; ++pageIndex)
    if (getTabbablePDFElementsInPage(stores, pageIndex)) return true;
  for (let pageIndex = lastPageInDOM + 1; pageIndex < getTotalPages(stores); ++pageIndex)
    if (getTabbablePDFElementsInPage(stores, pageIndex)) return true;
  return false;
};
export const getDocumentStatus = stores => stores.accessibilityStore.documentStatus;
export const getIsActiveElementInContextMenu = stores => stores.accessibilityStore.isActiveElementInContextMenu;
export const getFocusElementCallback = (stores, currElementName) => currElement => {
  const elementToFocus = getUIElementToFocus(stores);
  const shouldFocus = elementToFocus && elementToFocus === currElementName;
  if (currElement && shouldFocus) {
    window.requestAnimationFrame(() => currElement.focus());
    stores.accessibilityStore.setUIElementToFocus(null);
  }
};
export const getIsCaretSetByFocus = stores => stores.accessibilityStore.isCaretSetByFocus;
